import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Skeleton,
  Stack,
  CardActionArea,
} from '@mui/material';
import { ArrowForward, CameraAlt } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService, Product } from '../services/api';
import { marketingPhotoPlateSx } from '../theme/marketingTokens';

/**
 * Homepage teaser for the public product demos experience.
 * Intentionally does not list demo products on the home page — just a link/image CTA.
 */
export const ProductShowcaseSection: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await apiService.getProductShowcase();
        if (!cancelled) {
          setPreview(data[0] ?? null);
        }
      } catch {
        if (!cancelled) {
          setPreview(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const image =
    preview?.images?.find((url) => typeof url === 'string' && url.trim()) ||
    '/placeholder-camera.svg';

  const goToDemos = () => navigate('/product-demos');

  return (
    <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'background.paper' }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            {t('public.home.showcase.title')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: '640px', mx: 'auto', fontSize: { xs: '1rem', md: '1.15rem' } }}
          >
            {t('public.home.showcase.subtitle')}
          </Typography>
        </Box>

        <CardActionArea
          onClick={goToDemos}
          aria-label={t('public.home.showcase.cta')}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            // Only plate when a real photo is showing: the loading skeleton and
            // the no-preview state draw in light-on-dark and would vanish.
            bgcolor: !loading && preview ? marketingPhotoPlateSx.backgroundColor : 'grey.50',
            display: 'block',
            transition: 'box-shadow 0.25s ease, transform 0.25s ease',
            '&:hover': {
              boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 200, md: 260 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            {loading ? (
              <Skeleton
                variant="rectangular"
                sx={{ width: '100%', maxWidth: 420, height: { xs: 160, md: 200 }, borderRadius: 2 }}
              />
            ) : preview ? (
              <Box
                component="img"
                src={image}
                alt={preview.name || preview.model || t('public.home.showcase.title')}
                sx={{
                  maxWidth: '100%',
                  maxHeight: { xs: 180, md: 220 },
                  objectFit: 'contain',
                }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.src = '/placeholder-camera.svg';
                }}
              />
            ) : (
              <Stack alignItems="center" spacing={1} sx={{ color: 'text.secondary', py: 4 }}>
                <CameraAlt sx={{ fontSize: 48, opacity: 0.5 }} />
                <Typography variant="body1" fontWeight={600}>
                  {t('public.home.showcase.cta')}
                </Typography>
              </Stack>
            )}
          </Box>
        </CardActionArea>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={goToDemos}
            sx={{ fontWeight: 600, textTransform: 'none', px: 3 }}
          >
            {t('public.home.showcase.cta')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ProductShowcaseSection;
